import { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { BRAND } from "@/lib/site";

const NAV_GROUPS = [
  {
    label: "For Homeowners",
    testid: "nav-homeowners-dropdown",
    items: [
      { to: "/for-homeowners", label: "The Owner's Hub" },
      { to: "/co-hosting", label: "Airbnb & VRBO Co-Hosting" },
      { to: "/property-management", label: "Property Management" },
      { to: "/cleaning", label: "Cleaning & Turnovers" },
      { to: "/maintenance", label: "Maintenance" },
      { to: "/inspections", label: "Property Inspections" },
      { to: "/start-your-airbnb", label: "Start Your Airbnb" },
      { to: "/for-homeowners#calculator", label: "Profit Calculator" },
      { to: "/guides", label: "Digital Guides & Ebooks" },
    ],
  },
  {
    label: "For Guests",
    testid: "nav-guests-dropdown",
    items: [
      { to: "/guest-tools", label: "Guest Tools" },
      { to: "/bermuda-bucket-list", label: "Bucket List Challenge" },
      { to: "/guest-tools/getting-around-bermuda", label: "Getting Around Bermuda" },
      { to: "/guest-tools/plan-your-stay", label: "Plan Your Stay" },
      { to: "/calendar", label: "My Bermuda Calendar" },
      { to: "/cleaning#guest-cleaning", label: "Guest Cleaning" },
      { to: "/loyalty", label: "The Co-Host Circle" },
      { to: "/guest-support", label: "Guest Support" },
    ],
  },
];

const ABOUT_GROUP = {
  label: "About",
  testid: "nav-about-dropdown",
  items: [
    { to: "/about", label: "Our Story" },
    { to: "/resources", label: "Resource Center" },
    { to: "/faq", label: "FAQ" },
    { to: "/contact", label: "Contact" },
  ],
};

const MOBILE_GROUPS = [
  {
    label: "Homeowners",
    items: [
      { to: "/for-homeowners", label: "For Homeowners" },
      { to: "/co-hosting", label: "Co-Hosting" },
      { to: "/property-management", label: "Management" },
      { to: "/cleaning", label: "Cleaning" },
      { to: "/maintenance", label: "Maintenance" },
      { to: "/inspections", label: "Inspections" },
      { to: "/pricing", label: "Pricing" },
      { to: "/start-your-airbnb", label: "Start Your Airbnb" },
      { to: "/guides", label: "Digital Guides" },
    ],
  },
  {
    label: "Guests",
    items: [
      { to: "/plan", label: "Plan My Trip" },
      { to: "/guest-tools", label: "Guest Tools" },
      { to: "/bermuda-bucket-list", label: "Bucket List Challenge" },
      { to: "/guest-tools/getting-around-bermuda", label: "Getting Around" },
      { to: "/guest-tools/plan-your-stay", label: "Plan Your Stay" },
      { to: "/calendar", label: "My Bermuda Calendar" },
      { to: "/cleaning#guest-cleaning", label: "Guest Cleaning" },
      { to: "/loyalty", label: "Co-Host Circle" },
      { to: "/guest-support", label: "Guest Support" },
    ],
  },
  {
    label: "Company",
    items: [
      { to: "/", label: "Home" },
      { to: "/about", label: "About" },
      { to: "/resources", label: "Resources" },
      { to: "/faq", label: "FAQ" },
      { to: "/contact", label: "Contact" },
      { to: "/owner/login", label: "Owner Login" },
    ],
  },
];

const DARK_TOP_PATHS = ["/", "/services", "/co-hosting", "/property-management", "/cleaning", "/maintenance", "/inspections", "/for-homeowners", "/start-your-airbnb", "/about", "/owner/login", "/owner", "/bermuda-bucket-list", "/guest-tools", "/loyalty"];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const darkTop =
    DARK_TOP_PATHS.includes(pathname) ||
    pathname.startsWith("/guest-tools/") ||
    (pathname.startsWith("/services/") && pathname !== "/services") ||
    (pathname.startsWith("/resources/") && pathname !== "/resources");
  const onDark = darkTop && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const linkCls = ({ isActive }) =>
    `link-underline text-[11px] font-medium uppercase tracking-[0.14em] transition-colors duration-300 ${
      onDark
        ? isActive ? "text-bermuda" : "text-ivory/85 hover:text-white"
        : isActive ? "text-bermuda-dark" : "text-navy/80 hover:text-navy"
    }`;

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "backdrop-blur-xl bg-ivory/85 border-b border-navy/5 shadow-sm"
            : onDark
              ? "bg-gradient-to-b from-navy/70 via-navy/25 to-transparent"
              : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-4 px-5 py-3.5 lg:px-10">
          <Link to="/" data-testid="nav-logo" className="shrink-0">
            <img
              src={onDark ? "/logo-horizontal-white.png" : "/logo-horizontal.png"}
              alt="The Bermuda Co-Host"
              className="h-11 w-auto transition-opacity duration-300 lg:h-12"
            />
          </Link>

          <nav className="hidden xl:flex items-center gap-7" aria-label="Primary">
            {[...NAV_GROUPS].map((g) => (
              <div key={g.label} className="group relative">
                <button data-testid={g.testid} className={`flex items-center gap-1 text-[11px] font-medium uppercase tracking-[0.14em] transition-colors ${onDark ? "text-ivory/85 hover:text-white" : "text-navy/80 hover:text-navy"}`}>
                  {g.label} <ChevronDown size={12} />
                </button>
                <div className="invisible absolute left-0 top-full z-50 w-72 translate-y-2 pt-3 opacity-0 transition-all duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                  <div className="border border-navy/5 bg-white p-3 shadow-xl shadow-navy/10">
                    {g.items.map((item) => (
                      <Link key={item.to + item.label} to={item.to} data-testid={`nav-item-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="block px-3 py-2 text-[13px] text-navy/75 hover:bg-sand-light hover:text-navy transition-colors">
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            <NavLink to="/pricing" data-testid="nav-link-pricing" className={linkCls}>
              Pricing
            </NavLink>
            <NavLink to="/guides" data-testid="nav-link-guides" className={linkCls}>
              Guides
            </NavLink>
            <NavLink to="/plan" data-testid="nav-link-plan" className={linkCls}>
              Plan My Trip
            </NavLink>
            <div className="group relative">
              <button data-testid={ABOUT_GROUP.testid} className={`flex items-center gap-1 text-[11px] font-medium uppercase tracking-[0.14em] transition-colors ${onDark ? "text-ivory/85 hover:text-white" : "text-navy/80 hover:text-navy"}`}>
                {ABOUT_GROUP.label} <ChevronDown size={12} />
              </button>
              <div className="invisible absolute right-0 top-full z-50 w-56 translate-y-2 pt-3 opacity-0 transition-all duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                <div className="border border-navy/5 bg-white p-3 shadow-xl shadow-navy/10">
                  {ABOUT_GROUP.items.map((item) => (
                    <Link key={item.to} to={item.to} data-testid={`nav-item-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="block px-3 py-2 text-[13px] text-navy/75 hover:bg-sand-light hover:text-navy transition-colors">
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </nav>

          <div className="hidden xl:flex items-center gap-4 shrink-0">
            <Link to="/consultation" data-testid="nav-book-consultation-btn" className={`link-underline text-[11px] font-medium uppercase tracking-[0.14em] transition-colors ${onDark ? "text-ivory/85 hover:text-white" : "text-navy/80 hover:text-navy"}`}>
              Book a Consultation
            </Link>
            <Link
              to="/assessment"
              data-testid="nav-property-assessment-btn"
              className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 ${
                onDark ? "bg-bermuda text-navy hover:bg-ivory" : "bg-navy text-ivory hover:bg-bermuda-dark hover:text-white"
              }`}
            >
              Get a Property Assessment
            </Link>
          </div>

          <button data-testid="nav-mobile-menu-btn" onClick={() => setOpen(true)} className={`xl:hidden p-1 transition-colors ${onDark ? "text-ivory" : "text-navy"}`} aria-label="Open menu">
            <Menu size={26} strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-navy text-ivory"
          >
            <div className="flex items-center justify-between px-5 py-4">
              <img src="/logo-horizontal-white.png" alt="The Bermuda Co-Host" className="h-10 w-auto" />
              <button data-testid="nav-mobile-close-btn" onClick={() => setOpen(false)} aria-label="Close menu" className="p-1">
                <X size={26} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-6 pb-10 pt-4">
              {MOBILE_GROUPS.map((group) => (
                <div key={group.label} className="mb-6">
                  <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-bermuda/70">{group.label}</p>
                  {group.items.map((l) => (
                    <button
                      key={l.to + l.label}
                      data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => { setOpen(false); navigate(l.to); }}
                      className="block w-full border-b border-white/10 py-2.5 text-left font-serif text-xl text-ivory/85 transition-colors hover:text-bermuda"
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              ))}
              <div className="mt-8 flex flex-col gap-3">
                <Link to="/assessment" onClick={() => setOpen(false)} data-testid="mobile-nav-assessment-btn" className="bg-bermuda px-6 py-4 text-center text-xs font-semibold uppercase tracking-[0.18em] text-navy">
                  Get a Property Assessment
                </Link>
                <Link to="/consultation" onClick={() => setOpen(false)} data-testid="mobile-nav-consultation-btn" className="border border-ivory/30 px-6 py-4 text-center text-xs font-semibold uppercase tracking-[0.18em] text-ivory">
                  Book a Consultation
                </Link>
                <a href={BRAND.phoneHref} data-testid="mobile-nav-phone" className="pt-3 text-center text-sm text-ivory/60">{BRAND.phone}</a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
